import React, { useState, useEffect } from "react";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      // Detect active section
      const sections = ["home", "about", "experience", "projects", "contact"];
      const current = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", id: "home" },
    { name: "About", id: "about" },
    { name: "Experience", id: "experience" },
    { name: "Projects", id: "projects" },
    { name: "Contact", id: "contact" },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 w-full z-[60] transition-all duration-500 ${
        scrolled
          ? "bg-zinc-950/80 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
        {/* Brand / Logo */}
        <a
          href="#home"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("home");
          }}
          className="group flex items-center gap-2 sm:gap-3 z-50"
        >
          <div
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-white/[0.03] border border-white/[0.08] 
                        flex items-center justify-center group-hover:bg-white/[0.05] 
                        group-hover:border-white/[0.12] transition-all duration-500"
          >
            <span
              className="text-white/50 group-hover:text-white/70 text-[10px] sm:text-xs font-light 
                           transition-colors duration-500"
            >
              MJH
            </span>
          </div>
          <div className="hidden md:block">
            <span
              className="text-white/70 group-hover:text-white/90 text-xs sm:text-sm font-light 
                           transition-colors duration-500"
            >
              Mohammed Junaid Hussain
            </span>
          </div>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(item.id);
              }}
              className={`text-xs tracking-wider uppercase font-light transition-colors duration-500 ${
                activeSection === item.id
                  ? "text-white/80"
                  : "text-white/40 hover:text-white/60"
              }`}
            >
              {item.name}
            </a>
          ))}
        </nav>

        {/* Mobile Menu Button & Resume */}
        <div className="flex items-center gap-3 sm:gap-4 z-50">
          {/* Resume Button */}
          <a
            href="/Final_Resume_wp.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative px-4 sm:px-6 py-2 sm:py-2.5 overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-white/[0.02] border border-white/[0.08] rounded-full
                          group-hover:bg-white/[0.04] group-hover:border-white/[0.15] 
                          transition-all duration-500"
            ></div>
            <span
              className="relative text-white/60 group-hover:text-white/80 text-[10px] sm:text-xs tracking-wider 
                           uppercase font-light transition-colors duration-500 flex items-center gap-1 sm:gap-2"
            >
              <span className="hidden xs:inline">Resume</span>
              <span className="xs:hidden">CV</span>
              <svg
                className="w-3 h-3 group-hover:translate-y-[-2px] transition-transform duration-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                />
              </svg>
            </span>
          </a>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg bg-white/[0.03] border border-white/[0.08] 
                     hover:bg-white/[0.05] hover:border-white/[0.12] transition-all duration-300"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-4 flex flex-col justify-between">
              <span
                className={`block h-0.5 w-full bg-white/60 transition-all duration-300 ${
                  mobileMenuOpen ? "rotate-45 translate-y-[7px]" : ""
                }`}
              ></span>
              <span
                className={`block h-0.5 w-full bg-white/60 transition-all duration-300 ${
                  mobileMenuOpen ? "opacity-0" : ""
                }`}
              ></span>
              <span
                className={`block h-0.5 w-full bg-white/60 transition-all duration-300 ${
                  mobileMenuOpen ? "-rotate-45 -translate-y-[7px]" : ""
                }`}
              ></span>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 bg-zinc-950/95 backdrop-blur-xl transition-all duration-300 ${
          mobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        style={{ top: "72px" }}
      >
        <nav className="flex flex-col items-center justify-center h-full gap-8 px-6">
          {navItems.map((item, index) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(item.id);
              }}
              className={`text-2xl sm:text-3xl font-light transition-all duration-500 ${
                activeSection === item.id
                  ? "text-white/90 scale-110"
                  : "text-white/40 hover:text-white/70 hover:scale-105"
              }`}
              style={{
                animation: mobileMenuOpen
                  ? `fadeInUp 0.5s ease-out ${index * 0.1}s both`
                  : "none",
              }}
            >
              {item.name}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
}
